// ENG-2277 (HTML Forms): the fonts a Form prints in, and how a font named in a form maps onto them.
// Each target has the same letter widths as the fonts it stands in for, so a line breaks at the
// same word in the PDF as in the original form and in the editor.
//
// ?url imports give Vite the hashed asset URL without emitting the font into the JS bundle; the
// file is fetched only when a document uses that family.
import LatoBold from "assets/fonts/Lato/Lato-Bold.ttf?url";
import LatoBoldItalic from "assets/fonts/Lato/Lato-BoldItalic.ttf?url";
import LatoRegularItalic from "assets/fonts/Lato/Lato-Italic.ttf?url";
import LatoRegular from "assets/fonts/Lato/Lato-Regular.ttf?url";
// Liberation Sans 2.x is Google's Arimo outlines under the Liberation name (see its OFL.txt):
// metric-compatible with Arial and Helvetica.
import LiberationSansBold from "assets/fonts/LiberationSans/LiberationSans-Bold.ttf?url";
import LiberationSansBoldItalic from "assets/fonts/LiberationSans/LiberationSans-BoldItalic.ttf?url";
import LiberationSansItalic from "assets/fonts/LiberationSans/LiberationSans-Italic.ttf?url";
import LiberationSansRegular from "assets/fonts/LiberationSans/LiberationSans-Regular.ttf?url";
import TinosBold from "assets/fonts/Tinos/Tinos-Bold.ttf?url";
import TinosBoldItalic from "assets/fonts/Tinos/Tinos-BoldItalic.ttf?url";
import TinosItalic from "assets/fonts/Tinos/Tinos-Italic.ttf?url";
import TinosRegular from "assets/fonts/Tinos/Tinos-Regular.ttf?url";

export const FORM_FONT_FAMILIES = {
  sans: "LiberationSans",
  serif: "Tinos",
  // One of the PDF standard fonts every reader carries, so it needs no file. react-pdf does not
  // derive Courier-Bold / Courier-Oblique from a font-weight, so the Form normalizer writes the
  // variant name explicitly.
  mono: "Courier",
  lato: "LatoRegular"
} as const;

export type PdfFontFace = {
  family: string;
  weight: 400 | 700;
  style: "normal" | "italic";
  src: string;
};

// The registered families a Form can print in. The editor loads the same files, so what the user
// sees while editing has the PDF's metrics.
export const PDF_FONT_FACES: readonly PdfFontFace[] = [
  {
    family: FORM_FONT_FAMILIES.sans,
    weight: 400,
    style: "normal",
    src: LiberationSansRegular
  },
  {
    family: FORM_FONT_FAMILIES.sans,
    weight: 400,
    style: "italic",
    src: LiberationSansItalic
  },
  {
    family: FORM_FONT_FAMILIES.sans,
    weight: 700,
    style: "normal",
    src: LiberationSansBold
  },
  {
    family: FORM_FONT_FAMILIES.sans,
    weight: 700,
    style: "italic",
    src: LiberationSansBoldItalic
  },
  {
    family: FORM_FONT_FAMILIES.serif,
    weight: 400,
    style: "normal",
    src: TinosRegular
  },
  {
    family: FORM_FONT_FAMILIES.serif,
    weight: 400,
    style: "italic",
    src: TinosItalic
  },
  {
    family: FORM_FONT_FAMILIES.serif,
    weight: 700,
    style: "normal",
    src: TinosBold
  },
  {
    family: FORM_FONT_FAMILIES.serif,
    weight: 700,
    style: "italic",
    src: TinosBoldItalic
  },
  {
    family: FORM_FONT_FAMILIES.lato,
    weight: 400,
    style: "normal",
    src: LatoRegular
  },
  {
    family: FORM_FONT_FAMILIES.lato,
    weight: 400,
    style: "italic",
    src: LatoRegularItalic
  },
  {
    family: FORM_FONT_FAMILIES.lato,
    weight: 700,
    style: "normal",
    src: LatoBold
  },
  {
    family: FORM_FONT_FAMILIES.lato,
    weight: 700,
    style: "italic",
    src: LatoBoldItalic
  }
];

const SANS = new Set([
  "arial",
  "helvetica",
  "helvetica neue",
  "liberation sans",
  "liberationsans",
  "arimo",
  "arialmt"
]);
const SERIF = new Set([
  "times",
  "times new roman",
  "timesnewroman",
  "tinos",
  "liberation serif"
]);
const MONO = new Set([
  "courier",
  "courier new",
  "couriernew",
  "cousine",
  "liberation mono",
  "consolas",
  "menlo",
  "monaco",
  "monospace"
]);
const LATO = new Set(["lato", "latoregular"]);
const INHERITED = new Set(["", "inherit", "initial", "unset", "revert"]);

/**
 * The PDF family a CSS font-family list prints in, or null when the list names no font (the
 * inherited font then applies). Names are tried in order, as a browser would; a generic family
 * ends the search, and a list of only unknown fonts prints in Lato.
 */
export const mapSourceFontFamily = (
  fontFamily: string | null | undefined
): string | null => {
  const names = (fontFamily ?? "")
    .split(",")
    .map((name) =>
      name
        .trim()
        .replace(/^["']|["']$/g, "")
        .trim()
        .toLowerCase()
    )
    .filter((name) => !INHERITED.has(name));
  if (names.length === 0) return null;

  for (const name of names) {
    if (SANS.has(name)) return FORM_FONT_FAMILIES.sans;
    if (SERIF.has(name) || name === "serif") return FORM_FONT_FAMILIES.serif;
    if (MONO.has(name)) return FORM_FONT_FAMILIES.mono;
    if (LATO.has(name) || name === "sans-serif") return FORM_FONT_FAMILIES.lato;
  }
  return FORM_FONT_FAMILIES.lato;
};
